import CachedIcon from "@mui/icons-material/Cached";
import { Card, FormControlLabel, Switch } from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import MDAvatar from "@/components/atoms/MDAvatar";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import useAuth from "@/contexts/auth-context";
import { useNotification } from "@/contexts/notification-context";
import { formatPhoneNumberBRSpaces } from "@/helpers/phone-helpers";
import { ChatMessages } from "@/messages/chat/chat.messages";
import customerPlatformService from "@/services/customer-platform.service";
import serviceQueueService from "@/services/service-queue.service";
import pxToRem from "@/themes/functions/pxToRem";

type Props = {
  messages: ChatMessages;
};

function readFlag(payload: unknown, keys: string[]): boolean {
  if (!payload || typeof payload !== "object") return false;

  const record = payload as Record<string, unknown>;
  const nested = record.data && typeof record.data === "object" ? (record.data as Record<string, unknown>) : null;
  const sources = nested ? [record, nested] : [record];

  for (const source of sources) {
    for (const key of keys) {
      if (source[key] !== undefined && source[key] !== null) {
        return Boolean(source[key]);
      }
    }
  }

  return false;
}

function readCount(payload: unknown): number {
  if (!payload || typeof payload !== "object") return 0;
  const record = payload as Record<string, unknown>;
  const value = record.count ?? record.Count;
  return typeof value === "number" ? value : Number(value) || 0;
}

function WhatsAppAccountInfo({ messages }: Props) {
  const { user } = useAuth();
  const {
    custWhatsPlatform,
    setCustWhatsPlatform,
    isLoadingCustWhatsPlatform,
    mutate: refreshChats,
  } = useNotification();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRefreshingChats, setIsRefreshingChats] = useState(false);
  const [openConversationsCount, setOpenConversationsCount] = useState(0);
  const [isAttendantActive, setIsAttendantActive] = useState(false);
  const [isUpdatingAvailability, setIsUpdatingAvailability] = useState(false);
  const [isInAnyQueue, setIsInAnyQueue] = useState(false);
  const [hasLoadedQueueStatus, setHasLoadedQueueStatus] = useState(false);

  // Admin/Gerente na fila também precisa do switch. A membership é a fonte da verdade,
  // não o checkPermissions (que escondia o controle para quem tem perfil Administrador).
  const showAttendantControls = isInAnyQueue && hasLoadedQueueStatus;

  const loadAttendantStats = useCallback(async () => {
    try {
      const [queueStatus, openCount] = await Promise.all([
        serviceQueueService.getAttendantQueueStatus(),
        serviceQueueService.getOpenConversationsCount(),
      ]);
      setIsInAnyQueue(readFlag(queueStatus, ["isInAnyQueue", "IsInAnyQueue"]));
      setIsAttendantActive(readFlag(queueStatus, ["active", "Active"]));
      setOpenConversationsCount(readCount(openCount));
    } catch {
      try {
        const userId = user?.id;
        const [memberships, availability, openCount] = await Promise.all([
          userId ? serviceQueueService.getByUser(userId) : Promise.resolve([]),
          serviceQueueService.getAttendantAvailability(),
          serviceQueueService.getOpenConversationsCount(),
        ]);
        setIsInAnyQueue(Array.isArray(memberships) && memberships.length > 0);
        setIsAttendantActive(readFlag(availability, ["active", "Active"]));
        setOpenConversationsCount(readCount(openCount));
      } catch {
        setIsInAnyQueue(false);
        setIsAttendantActive(false);
        setOpenConversationsCount(0);
      }
    } finally {
      setHasLoadedQueueStatus(true);
    }
  }, [user?.id]);

  useEffect(() => {
    loadAttendantStats();
  }, [loadAttendantStats]);

  const refreshAll = useCallback(async () => {
    try {
      setIsUpdating(true);
      setIsRefreshingChats(true);

      const promises: Promise<unknown>[] = [];

      if (custWhatsPlatform) {
        promises.push(
          customerPlatformService.whatsappUpdateGeneralInfo({
            whatsPlatform: custWhatsPlatform,
          })
        );
      }

      promises.push(refreshChats(undefined, { revalidate: true }));
      promises.push(loadAttendantStats());

      const results = await Promise.all(promises);

      if (results[0] && custWhatsPlatform) {
        setCustWhatsPlatform(results[0] as typeof custWhatsPlatform);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error("Failed to refresh:", error);
      }
    } finally {
      setIsUpdating(false);
      setIsRefreshingChats(false);
    }
  }, [custWhatsPlatform, setCustWhatsPlatform, refreshChats, loadAttendantStats]);

  const handleAvailabilityChange = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsUpdatingAvailability(true);
    try {
      const result = await serviceQueueService.setAttendantAvailability(checked);
      setIsAttendantActive(readFlag(result, ["active", "Active"]));
    } catch {
      // keep previous state on error
    } finally {
      setIsUpdatingAvailability(false);
    }
  };

  return (
    <Card sx={{ mb: 2, overflow: "hidden", flexShrink: 0 }}>
      <MDBox p={2} display="flex" alignItems="center" gap={1.5}>
        <MDAvatar
          src={custWhatsPlatform?.accountImage}
          alt={custWhatsPlatform?.displayName || messages.profileAltText}
          size="md"
          shadow="sm"
          bgColor="light"
          sx={{ flexShrink: 0 }}
        />

        <MDBox height="100%" mt={0.25} lineHeight={1} sx={{ minWidth: 0, flexGrow: 1 }}>
          <MDTypography variant="h6" fontWeight="medium" sx={{ fontSize: pxToRem(18) }}>
            {isLoadingCustWhatsPlatform
              ? messages.loadingAccountInfo
              : custWhatsPlatform?.displayName || messages.noDataFound}
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text"
            fontWeight="medium"
            sx={{ fontSize: pxToRem(12.2) }}
          >
            {messages.whatsappLabel}
            {isLoadingCustWhatsPlatform
              ? messages.loadingAccountInfo
              : custWhatsPlatform?.displayNumber
                ? formatPhoneNumberBRSpaces(custWhatsPlatform.displayNumber)
                : messages.noNumberFound}
          </MDTypography>
        </MDBox>
        <MDButton
          variant="text"
          color="primary"
          size="large"
          onClick={refreshAll}
          sx={{ ml: "auto", flexShrink: 0 }}
          isLoading={isUpdating || isRefreshingChats}
          title="Atualizar conta e chats"
        >
          <CachedIcon />
        </MDButton>
      </MDBox>

      {showAttendantControls && (
        <MDBox px={2} pb={1.5} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <MDBox display="flex" alignItems="center" gap={0.75}>
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {messages.openConversationsLabel}
            </MDTypography>
            <MDTypography variant="h6" fontWeight="bold" color="primary">
              {openConversationsCount}
            </MDTypography>
          </MDBox>

          <FormControlLabel
            control={
              <Switch
                checked={isAttendantActive}
                onChange={handleAvailabilityChange}
                disabled={isUpdatingAvailability}
                color="primary"
              />
            }
            label={isAttendantActive ? messages.attendantActiveLabel : messages.attendantInactiveLabel}
          />
        </MDBox>
      )}
    </Card>
  );
}

export default WhatsAppAccountInfo;
